import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { Loader2, AlertTriangle, Maximize2, Minimize2 } from "lucide-react";
import { type Task } from "@shared/schema";
import { WeeklyCalendar } from "@/components/ui/weekly-calendar";
import { useState, useEffect } from "react";

/**
 * Samostatná stránka s týždenným plánovačom, ktorá je určená na zobrazenie v novom okne.
 * Nemá žiadny layout ani menu - obsahuje len samotný plánovač.
 */
export default function StandalonePlanner() {
  const { user } = useAuth();
  const [animate, setAnimate] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const { data: tasksRaw, isLoading, error } = useQuery<any>({
    queryKey: ["/api/tasks"],
    queryFn: async () => {
      const response = await fetch('/api/tasks', {
        credentials: 'include',
        headers: { 'Accept': 'application/json' },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);
    },
  });

  // Normalizujeme tasks - podporujeme oba formáty
  const tasks: Task[] = Array.isArray(tasksRaw) ? tasksRaw.map((task: any) => ({
    ...task,
    isStarred: task.isStarred !== undefined ? task.isStarred : (task.is_starred ?? false),
    isInvoiced: task.isInvoiced !== undefined ? task.isInvoiced : (task.is_invoiced ?? false),
    dueDate: task.dueDate || task.due_date || null,
  })) : [];

  // Filtrujeme vyfaktúrované úlohy z označených hviezdičkou
  const starredTasks = tasks.filter(task => task.isStarred && !task.isInvoiced);
  
  // Aktivácia animácií po načítaní komponentu
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimate(true);
    }, 300);
    
    // Nastavenie titulku stránky
    document.title = "Týždenný plánovač | TaskkroW";
    
    return () => clearTimeout(timer);
  }, []);

  // Funkcia pre prepnutie fullscreen módu
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch((err) => {
        console.error('Chyba pri aktivácii fullscreen:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch((err) => {
        console.error('Chyba pri ukončení fullscreen:', err);
      });
    }
  };

  // Sledovanie zmien fullscreen stavu
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen gap-2 text-destructive">
        <AlertTriangle className="h-5 w-5" />
        <p>Chyba pri načítaní údajov</p>
      </div>
    );
  }

  return (
    <div className={`${isFullscreen ? 'p-2' : 'p-4'} min-h-screen bg-background`}>
      <Card className={`${isFullscreen ? 'max-w-none h-full' : 'w-full'} min-w-0`}>
        <CardContent className={`${isFullscreen ? 'p-2' : 'p-4'} ${isFullscreen ? 'h-full' : ''}`}>
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-xl font-bold">Týždenný plánovač</h1>
            <div className="flex items-center gap-4">
              {user && <p className="text-sm text-muted-foreground">Používateľ: {user.email}</p>}
              <Button
                variant="outline"
                size="sm"
                onClick={toggleFullscreen}
                className="flex items-center gap-2"
                title={isFullscreen ? "Ukončiť fullscreen" : "Otvoriť na celú obrazovku"}
              >
                {isFullscreen ? (
                  <>
                    <Minimize2 className="h-4 w-4" />
                    <span className="hidden sm:inline">Ukončiť fullscreen</span>
                  </>
                ) : (
                  <>
                    <Maximize2 className="h-4 w-4" />
                    <span className="hidden sm:inline">Fullscreen</span>
                  </>
                )}
              </Button>
            </div>
          </div>
          <div className={isFullscreen ? 'h-[calc(100vh-8rem)]' : ''}>
            <WeeklyCalendar tasks={starredTasks} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}